import * as Yup from 'yup';

// Register Validation
export const registerValidationSchema = Yup.object({
  name: Yup.string().min(3, 'Name must be at least 3 characters').required('Name is required'),
  email: Yup.string().email('Invalid email format').required('Email is required'),
  organization: Yup.string().min(3, 'Organization Name must be at least 3 characters').required('Organization Name is required'),
  phone: Yup.string().matches(/^[0-9]+$/, 'Phone number must be digits only').min(10, 'Phone number must be at least 10 digits').required('Phone number is required'),
  password: Yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
  password_confirmation: Yup.string().oneOf([Yup.ref('password'), null], 'Passwords do not match').required('Confirm Password is required'),
});

// Login Validation
export const loginValidationSchema = Yup.object({
  email: Yup.string().email('Invalid email format').required('Email is required'),
  password: Yup.string().required('Password is required'),
});



export const userValidationSchema = Yup.object({
  username: Yup.string()
    .min(3, 'Username must be at least 3 characters')
    .required('Username is required'),

  password: Yup.string()
    .min(6, 'Password must be at least 6 characters')
    .required('Password is required'),

  password_confirmation: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Passwords do not match')
    .required('Confirm Password is required'),

  fullname: Yup.string()
    .min(3, 'Full Name must be at least 3 characters')
    .required('Full Name is required'),

  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),

  role: Yup.string()
    .required('Role is required'),
  vendor_codes: Yup.array()
    .of(
      Yup.string()
        .trim()
        .max(50, 'Vendor code cannot exceed 50 characters')
    )
    .default([])
    .when('role', {
      is: (role) => {
        if (!role) return false;
        const roleStr = String(role).toLowerCase().trim();
        return roleStr === 'vendor';
      },
      then: (schema) => schema.min(1, 'Vendor role requires at least one vendor code.').required('Vendor list is required for vendor role'),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  primary_vendor_code: Yup.string()
    .nullable()
    .when('role', {
      is: (role) => {
        if (!role) return false;
        const roleStr = String(role).toLowerCase().trim();
        return roleStr === 'vendor';
      },
      then: (schema) =>
        schema
          .required('Default vendor is required')
          .test(
            'primary-in-list',
            'Default vendor must be part of the selected vendor list',
            function (value) {
              const { vendor_codes } = this.parent;
              if (!value || !Array.isArray(vendor_codes)) return false;
              return vendor_codes.includes(value);
            }
          ),
      otherwise: (schema) => schema.notRequired().nullable(),
    }),
  pic_plant: Yup.string()
    .max(50, 'PIC Plant cannot exceed 50 characters')
    .nullable()
    .optional(),
});

// Edit form: password and PIN are not editable any more — the user sets them
// through an e-mailed reset link instead.
export const userEditValidationSchema = userValidationSchema.omit([
  'password',
  'password_confirmation',
]);



export const businessValidationSchema = Yup.object({
  name: Yup.string()
    .min(3, 'Name must be at least 3 characters')
    .max(255, 'Name cannot exceed 255 characters')
    .required('Name is required'),

  industry: Yup.string()
    .max(255, 'Industry cannot exceed 255 characters')
    .nullable(),

  logo_path: Yup.string()
    .max(255, 'Logo path cannot exceed 255 characters')
    .nullable(),

  status: Yup.string()
    .oneOf(['active', 'inactive'], 'Invalid status')
    .required('Status is required'),
});

export const vendorValidationSchema = Yup.object({
  vendor_code: Yup.string()
    .min(3, 'Vendor Code must be at least 3 characters')
    .required('Vendor Code is required'),

  vendor_name: Yup.string()
    .min(3, 'Vendor Name must be at least 3 characters')
    .max(255, 'Vendor Name cannot exceed 255 characters')
    .required('Vendor Name is required'),

  destination: Yup.string()
    .min(2, 'Destination must be at least 2 characters')
    .max(255, 'Destination cannot exceed 255 characters')
    .required('Destination is required'),

  email: Yup.string()
    .email('Invalid email format')
    .required('Email is required'),

  pic: Yup.string()
    .min(3, 'Person in Charge (PIC) must be at least 3 characters')
    .max(100, 'PIC name cannot exceed 100 characters')
    .required('Person in Charge (PIC) is required'),

  phone_number: Yup.string()
    .matches(/^[0-9]+$/, 'Phone number must only contain digits')
    .min(10, 'Phone number must be at least 10 digits')
    .max(15, 'Phone number cannot exceed 15 digits')
    .required('Phone number is required'),

  address: Yup.string()
    .min(5, 'Address must be at least 5 characters')
    .max(500, 'Address cannot exceed 500 characters')
    .required('Address is required'),

  // These two were labelled with "*" but had no rule here at all, so the form
  // submitted empty values and the API rejected them with a 400 — the user saw
  // a generic failure instead of an inline message. Limits mirror
  // CreateVendorDto (pic_delivery max 50, email_delivery max 70).
  pic_delivery: Yup.string()
    .max(50, 'Delivery PIC cannot exceed 50 characters')
    .required('Delivery PIC is required'),

  email_delivery: Yup.string()
    .email('Invalid email format')
    .max(70, 'Delivery email cannot exceed 70 characters')
    .required('Delivery email is required'),

  fg: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const trimmed = originalValue.trim();
        if (trimmed === '') return NaN;
        const normalized = trimmed.replace(/\./g, '').replace(',', '.');
        const parsed = Number(normalized);
        return Number.isNaN(parsed) ? NaN : parsed;
      }
      return value;
    })
    .typeError('FG (Finished Goods) must be a valid number')
    .min(0, 'FG cannot be negative')
    .required('FG is required'),

  wip: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const trimmed = originalValue.trim();
        if (trimmed === '') return NaN;
        const normalized = trimmed.replace(/\./g, '').replace(',', '.');
        const parsed = Number(normalized);
        return Number.isNaN(parsed) ? NaN : parsed;
      }
      return value;
    })
    .typeError('WIP (Work In Progress) must be a valid number')
    .min(0, 'WIP cannot be negative')
    .required('WIP is required'),
});




export const menuValidationSchema = Yup.object({
  menu_id: Yup.string()
    .min(3, 'Menu ID must be at least 3 characters')
    .max(50, 'Menu ID cannot exceed 50 characters')
    .required('Menu ID is required'),
  menu_name: Yup.string()
    .min(3, 'Menu name must be at least 3 characters')
    .max(100, 'Menu name cannot exceed 100 characters')
    .required('Menu name is required'),
  menu_path: Yup.string()
    .max(200, 'Menu path cannot exceed 200 characters'),
  menu_icon: Yup.string()
    .max(100, 'Menu icon cannot exceed 100 characters'),
  menu_order: Yup.number()
    .integer('Menu order must be an integer')
    .min(0, 'Menu order must be 0 or greater'),
  parent_menu_id: Yup.string()
    .max(50, 'Parent menu ID cannot exceed 50 characters'),
  description: Yup.string(),
  is_active: Yup.boolean(),
});


export const mastergroupingValidationSchema = Yup.object({
  group_name: Yup.string()
    .min(3, 'Group Name must be at least 3 characters')
    .max(255, 'Group Name cannot exceed 255 characters')
    .required('Group Name is required'),
});

export const shiftTimeValidationSchema = Yup.object({
  shift_code: Yup.string()
    .required('Shift code is required'),
  time: Yup.string()
    .matches(/^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/, 'Time must be in HH:mm format')
    .required('Time is required'),
  is_active: Yup.boolean().optional(),
});

export const roleValidationSchema = Yup.object({
  role_id: Yup.string()
    .min(3, 'Role ID must be at least 3 characters')
    .max(50, 'Role ID cannot exceed 50 characters')
    .required('Role ID is required'),

  role_name: Yup.string()
    .min(3, 'Role name must be at least 3 characters')
    .max(255, 'Role name cannot exceed 255 characters')
    .required('Role name is required'),

  role_description: Yup.string()
    .min(10, 'Description must be at least 10 characters')
    .max(500, 'Description cannot exceed 500 characters')
    .required('Description is required'),

  is_active: Yup.boolean()
    .required('Status is required'),

  is_vendor: Yup.boolean()
    .required('Is vendor is required'),
});

export const rolepermissionValidationSchema = Yup.object({
  name: Yup.string()
    .min(3, 'Name must be at least 3 characters')
    .max(255, 'Name cannot exceed 255 characters')
    .required('Name is required'),
});




export const forecastValidationSchema = Yup.object({
  name: Yup.string()
    .min(3, 'Name must be at least 3 characters')
    .max(255, 'Name cannot exceed 255 characters')
    .required('Name is required'),
});

export const forecastsupplierValidationSchema = Yup.object({
  name: Yup.string()
    .min(3, 'Name must be at least 3 characters')
    .max(255, 'Name cannot exceed 255 characters')
    .required('Name is required'),
});



export const materialValidationSchema = Yup.object({
  vendor_code: Yup.string()
    .min(3, 'Vendor Code must be at least 3 characters')
    .required('Vendor Code is required'),

  matl_code: Yup.string()
    .min(3, 'Material Code must be at least 3 characters')
    .required('Material Code is required'),

  item_no: Yup.string()
    .min(3, 'Item No must be at least 3 characters')
    .required('Item No is required'),

  sebango: Yup.string()
    .min(3, 'Sebango must be at least 3 characters')
    .required('Sebango is required'),

  part_name: Yup.string()
    .min(3, 'Part Name must be at least 3 characters')
    .required('Part Name is required'),

  project: Yup.string()
    .min(3, 'Project must be at least 3 characters')
    .required('Project is required'),

  part_level: Yup.number()
    .typeError('Part Level must be a number')
    .integer('Part Level must be an integer')
    .min(0, 'Part Level cannot be negative')
    .required('Part Level is required'),

  fg: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const trimmed = originalValue.trim();
        if (trimmed === '') return NaN;
        const normalized = trimmed.replace(/\./g, '').replace(',', '.');
        const parsed = Number(normalized);
        return Number.isNaN(parsed) ? NaN : parsed;
      }
      return value;
    })
    .typeError('FG (Finished Goods) must be a valid number')
    .min(0, 'FG cannot be negative')
    .required('FG is required'),

  wip: Yup.number()
    .transform((value, originalValue) => {
      if (typeof originalValue === 'string') {
        const trimmed = originalValue.trim();
        if (trimmed === '') return NaN;
        const normalized = trimmed.replace(/\./g, '').replace(',', '.');
        const parsed = Number(normalized);
        return Number.isNaN(parsed) ? NaN : parsed;
      }
      return value;
    })
    .typeError('WIP (Work In Progress) must be a valid number')
    .min(0, 'WIP cannot be negative')
    .required('WIP is required'),
});


export const configValidationSchema = Yup.object({
  config_type: Yup.string()
    .min(1, 'Config Type must be at least 1 character')
    .required('Config Type is required'),

  config_key: Yup.string()
    .min(1, 'Config Key must be at least 1 character')
    .required('Config Key is required'),

  config_value: Yup.string()
    .required('Config Value is required'),

  description: Yup.string()
    .min(1, 'Description must be at least 1 character')
    .required('Description is required'),

  is_active: Yup.boolean()
    .required('Active status is required'),
});

// Email Configuration Validation
export const emailTemplateValidationSchema = Yup.object({
  template_name: Yup.string()
    .min(3, 'Template name must be at least 3 characters')
    .max(100, 'Template name cannot exceed 100 characters')
    .required('Template name is required'),

  template_type: Yup.string()
    .min(3, 'Template type must be at least 3 characters')
    .max(50, 'Template type cannot exceed 50 characters')
    .required('Template type is required'),

  subject: Yup.string()
    .min(5, 'Subject must be at least 5 characters')
    .max(500, 'Subject cannot exceed 500 characters')
    .required('Subject is required'),

  body: Yup.string()
    .min(10, 'Body must be at least 10 characters')
    .required('Body is required'),

  variables: Yup.object()
    .nullable(),

  is_active: Yup.boolean()
    .required('Active status is required'),

  description: Yup.string()
    .max(500, 'Description cannot exceed 500 characters')
    .nullable(),
});

// Email Schedule Validation
export const emailScheduleValidationSchema = Yup.object({
  schedule_name: Yup.string()
    .min(3, 'Schedule name must be at least 3 characters')
    .max(100, 'Schedule name cannot exceed 100 characters')
    .required('Schedule name is required'),

  schedule_type: Yup.string()
    .oneOf(['minute', 'hourly', 'daily', 'weekly', 'monthly', 'custom'], 'Invalid schedule type')
    .required('Schedule type is required'),

  schedule_time: Yup.string()
    .required('Schedule time is required')
    .test('schedule-time-validation', 'Invalid schedule time format', function(value) {
      const { schedule_type } = this.parent;
      
      if (schedule_type === 'minute' || schedule_type === 'hourly') {
        // For minute and hourly, validate as number
        const numValue = parseInt(value);
        return !isNaN(numValue) && numValue > 0;
      } else if (schedule_type === 'custom') {
        // For custom, allow any string (cron expression)
        return true;
      } else {
        // For daily, weekly, monthly, validate as time format
        return /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/.test(value);
      }
    }),

  schedule_days: Yup.string()
    .max(20, 'Schedule days cannot exceed 20 characters')
    .nullable(),

  schedule_dates: Yup.string()
    .max(50, 'Schedule dates cannot exceed 50 characters')
    .nullable(),

  template_id: Yup.string()
    .max(26, 'Template ID cannot exceed 26 characters')
    .required('Template ID is required'),

  is_active: Yup.boolean()
    .required('Active status is required'),

  description: Yup.string()
    .max(500, 'Description cannot exceed 500 characters')
    .nullable(),

  api_url: Yup.string()
    .nullable()
    .test('api-url-validation', 'API URL must be a valid URL', function(value) {
      // If api_url is provided, it must be a valid URL
      if (value && value.trim() !== '') {
        try {
          new URL(value);
          return true;
        } catch {
          return false;
        }
      }
      // If empty, it's valid (optional)
      return true;
    }),

  api_method: Yup.string()
    .oneOf(['GET', 'POST', 'PUT', 'PATCH', 'DELETE'], 'API method must be one of: GET, POST, PUT, PATCH, DELETE')
    .nullable(),

  api_params: Yup.mixed()
    .nullable()
    .test('is-valid-json', 'API params must be valid JSON', function(value) {
      if (!value || value === '') return true;
      if (typeof value === 'object') return true;
      if (typeof value === 'string') {
        try {
          JSON.parse(value);
          return true;
        } catch {
          return false;
        }
      }
      return false;
    }),

  api_headers: Yup.mixed()
    .nullable()
    .test('is-valid-json', 'API headers must be valid JSON', function(value) {
      if (!value || value === '') return true;
      if (typeof value === 'object') return true;
      if (typeof value === 'string') {
        try {
          JSON.parse(value);
          return true;
        } catch {
          return false;
        }
      }
      return false;
    }),

  recipient_id: Yup.string()
    .max(26, 'Recipient ID cannot exceed 26 characters')
    .required('Recipient ID is required'),
});

// Helper function to validate multiple emails
const validateMultipleEmails = (value) => {
  if (!value || typeof value !== 'string') {
    return false;
  }

  // Split by comma or newline
  const emails = value
    .split(/[,\n]/)
    .map(email => email.trim())
    .filter(email => email.length > 0);

  if (emails.length === 0) {
    return false;
  }

  // Validate each email format
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  for (const email of emails) {
    if (!emailRegex.test(email)) {
      return false;
    }
  }

  // Check for duplicates
  const uniqueEmails = [...new Set(emails)];
  if (uniqueEmails.length !== emails.length) {
    return false;
  }

  return true;
};

// Email Recipients Validation
export const emailRecipientsValidationSchema = Yup.object({
  recipient_name: Yup.string()
    .min(3, 'Recipient name must be at least 3 characters')
    .max(100, 'Recipient name cannot exceed 100 characters')
    .required('Recipient name is required'),

  email_address: Yup.string()
    .required('Email address is required')
    .test('multiple-emails', 'Invalid email format or duplicate emails found', validateMultipleEmails),

  description: Yup.string()
    .max(500, 'Description cannot exceed 500 characters')
    .nullable(),

  is_active: Yup.boolean()
    .required('Active status is required'),

  is_vendor: Yup.boolean()
    .nullable(),

  vendor_code: Yup.string()
    .max(50, 'Vendor code cannot exceed 50 characters')
    .nullable()
    .when('is_vendor', {
      is: true,
      then: (schema) => schema.required('Vendor code is required when is vendor is checked'),
      otherwise: (schema) => schema.nullable(),
    }),
});
